// src/Redux/api/axiosInstance.js
// One axios instance for the whole app.
//  * adds the Bearer token
//  * platform owner: adds X-Owner-CompanyId (the company picked in the "Working on" bar)
//  * 401 with a lifecycle code (user / company inactive, subscription expired) → immediate sign-out + reason on the login page
//  * any other 401 → one silent token refresh, then retry; when that fails → sign-out

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_APP_API_URL;
export const SELECTED_COMPANY_KEY = 'authx_selected_company_id';
export const LOGOUT_REASON_KEY = 'authx_logout_reason';

// the API answers these codes when the account may no longer work at all
const FORCED_LOGOUT_CODES = ['USER_INACTIVE', 'COMPANY_INACTIVE', 'SUBSCRIPTION_EXPIRED', 'USER_NOT_FOUND'];

let _store;
export const injectStore = (store) => {
  _store = store;
};

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
});

const readUser = () => {
  try {
    return JSON.parse(localStorage.getItem('authx_user') || 'null');
  } catch {
    return null;
  }
};

// ─── Request interceptor ──────────────────────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authx_access_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;

    const state = _store?.getState();
    const isOwner = state?.auth?.isOwner ?? !!readUser()?.isOwner;
    if (isOwner) {
      let selected = state?.owner?.selectedCompanyId || null;
      if (!selected) {
        try {
          selected = localStorage.getItem(SELECTED_COMPANY_KEY);
        } catch {
          /* ignore */
        }
      }
      if (selected) config.headers['X-Owner-CompanyId'] = String(selected);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Response interceptor ─────────────────────────────────────────────────────
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const data = error.response?.data;

    if (status === 401 && originalRequest) {
      // account / company switched off → sign out right now, no refresh attempt
      if (FORCED_LOGOUT_CODES.includes(data?.code)) {
        forceLogout(data?.message || 'Your session has ended.');
        return Promise.reject(error);
      }

      if (!originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => failedQueue.push({ resolve, reject })).then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const accessToken = localStorage.getItem('authx_access_token');
        const refreshToken = localStorage.getItem('authx_refresh_token');
        if (!refreshToken) {
          isRefreshing = false;
          processQueue(error, null);
          forceLogout('Please sign in again.');
          return Promise.reject(error);
        }

        try {
          const res = await axios.post(`${BASE_URL}Auth/refresh`, { accessToken, refreshToken });
          const d = res.data.data;
          localStorage.setItem('authx_access_token', d.accessToken);
          localStorage.setItem('authx_refresh_token', d.refreshToken);
          if (d.user) localStorage.setItem('authx_user', JSON.stringify(d.user));
          if (d.menu) localStorage.setItem('authx_menu', JSON.stringify(d.menu));
          originalRequest.headers.Authorization = `Bearer ${d.accessToken}`;
          processQueue(null, d.accessToken);
          return api(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          forceLogout(refreshError.response?.data?.message || 'Your session has expired. Please sign in again.');
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
    }

    return Promise.reject(error);
  }
);

export function forceLogout(reason) {
  ['authx_access_token', 'authx_refresh_token', 'authx_user', 'authx_menu', SELECTED_COMPANY_KEY].forEach((k) => localStorage.removeItem(k));
  try {
    if (reason) sessionStorage.setItem(LOGOUT_REASON_KEY, reason);
  } catch {
    /* ignore */
  }
  if (!window.location.pathname.startsWith('/login')) window.location.href = '/login';
}

export default api;
